
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Alert, Button, Typography } from '@mui/material';
import { setAuthenticated, clearAuth, setError, setLoading } from './store/slices/authSlice';
import { RootState } from './types';
import Layout from './components/Layout';
import TicketsPage from './pages/TicketsPage';
import LoginPage from './pages/LoginPage';
import ClientsPage from './pages/ClientsPage';
import AlertsPage from './pages/AlertsPage';
import CorrelationsPage from './pages/CorrelationsPage';
import IOCsPage from './pages/IOCsPage';
import PlaybooksPage from './pages/PlaybooksPage';
import SessionDetailPage from './pages/SessionDetailPage';
import PlaybookTemplatesPage from './pages/PlaybookTemplatesPage';
import PlaybookTemplateEditorPage from './pages/PlaybookTemplateEditorPage';
import NotFoundPage from './pages/NotFoundPage';
import UserProfile from './components/users/UserProfile';
import { useAuth } from "react-oidc-context";
import { UserService } from './services/userService';
import CollapsibleSidebar from './components/sidebars/CollapsibleSidebar';
import OverviewPage from "./pages/OverviewPage";
import AIPerformancePage from "./pages/AIPerformancePage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import AIGymPage from "./pages/AIGymPage";
import OverviewPageDemo from "./pages/OverviewPageDemo";
import KitchenSinkPage from "./pages/KitchenSinkPage";
import AdminPage from "./pages/AdminPage";
import ClientLayout from './components/client/ClientLayout';
import ClientDashboardPage from './pages/client/ClientDashboardPage';
import ClientTicketsPage from './pages/client/ClientTicketsPage';
import ClientTicketDetailPage from './pages/client/ClientTicketDetailPage';
import ClientAnalyticsPage from './pages/client/ClientAnalyticsPage';
import LogoutCallbackPage from './pages/LogoutCallbackPage';
import { useLogout } from './hooks/useLogout';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();

    if (auth.isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!auth.isAuthenticated) {
        return <Navigate to="/login" />;
    }

    return <>{children}</>;
};

const App: React.FC = () => {
    const dispatch = useDispatch();
    const auth = useAuth();
    const logout = useLogout();
    const { error: authError } = useSelector((state: RootState) => state.auth);
    const [isUserSetupComplete, setIsUserSetupComplete] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const { user } = useSelector((state: RootState) => state.auth);
    const isAdmin = user?.role === 'global_admin';
    const isClient = user?.role === 'client_user';

    const handleSidebarToggle = (collapsed: boolean) => {
        setSidebarCollapsed(collapsed);
      };

    useEffect(() => {
        const setupUser = async () => {
            console.log('[setupUser]', { isAuthenticated: auth.isAuthenticated, isLoading: auth.isLoading, error: auth.error, user: auth.user, isUserSetupComplete });
            if (auth.isAuthenticated && auth.user && !isUserSetupComplete) {
                dispatch(setLoading(true));
                dispatch(setError(null)); // Clear any previous errors
                
                try {
                    // Store token in localStorage FIRST before making API calls
                    if (auth.user.access_token) {
                        localStorage.setItem('token', auth.user.access_token);
                    }

                    const email = auth.user.profile.email ?? '';
                    const emailLocalPart = email.split('@')[0] ?? '';
                    const userData = {
                        id: auth.user.profile.sub ?? '',
                        username: (() => {
                            const cognitoUsername = auth.user.profile["cognito:username"] as string;
                            const sub = auth.user.profile.sub ?? '';
                            return (cognitoUsername && cognitoUsername !== sub) ? cognitoUsername : (emailLocalPart || email);
                        })(),
                        email,
                        first_name: (auth.user.profile.given_name as string) || emailLocalPart || 'User',
                        last_name: (auth.user.profile.family_name as string) || '',
                        role: (auth.user.profile['custom:role'] as string) ?? 'client_user',
                    };

                    // Check if user exists in backend, create if not
                    console.log('[setupUser] calling ensureUserExists with', userData);
                    const backendUser = await UserService.ensureUserExists(userData);
                    console.log('[setupUser] ensureUserExists result', backendUser);

                    // Update Redux store with backend user data and Cognito authentication
                    dispatch(setAuthenticated({
                        isAuthenticated: true,
                        user: {
                            ...backendUser,
                            is_active: true, // Cognito users are typically active
                        },
                        token: auth.user.access_token
                    }));

                    setIsUserSetupComplete(true);
                    dispatch(setLoading(false));
                } catch (error: any) {
                    console.error('[setupUser] ERROR:', error);
                    console.error('Error setting up user:', error);
                    dispatch(setError(error.message || 'Failed to setup user'));
                    dispatch(setLoading(false));
                    
                    // Mark setup as complete even on error to prevent infinite loading
                    // The error will be displayed to the user
                    setIsUserSetupComplete(true);
                }
            } else if (!auth.isAuthenticated) {
                // Clear Redux auth state when not authenticated
                localStorage.removeItem('token');
                dispatch(clearAuth());
                setIsUserSetupComplete(false);
            }
        };

        setupUser();
    }, [auth.isAuthenticated, auth.user, dispatch, isUserSetupComplete]);

    if (auth.isLoading || (auth.isAuthenticated && !isUserSetupComplete)) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Handle Cognito errors
    if (auth.error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <div>
                    <h2>Authentication Error</h2>
                    <p>{auth.error.message}</p>
                    <button onClick={() => auth.signinRedirect()}>Try Again</button>
                </div>
            </Box>
        );
    }

    // Handle user setup errors
    if (auth.isAuthenticated && isUserSetupComplete && authError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
                <Box maxWidth="md" textAlign="center">
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            User Setup Error
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {authError}
                        </Typography>
                    </Alert>
                    <Button 
                        variant="contained" 
                        onClick={() => {
                            setIsUserSetupComplete(false);
                            dispatch(setError(null));
                        }}
                        sx={{ mr: 2 }}
                    >
                        Retry
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={() => logout()}
                    >
                        Sign Out
                    </Button>
                </Box>
            </Box>
        );
    }

    // If not authenticated, show login page
    if (!auth.isAuthenticated) {
        return (
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/logout-callback" element={<LogoutCallbackPage />} />
                <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
        );
    }

    // Client portal — completely separate shell, no analyst sidebar or drawers
    if (isClient) {
        return (
            <ClientLayout>
                <Routes>
                    <Route path="/" element={<Navigate to="/client/dashboard" replace />} />
                    <Route path="/login" element={<Navigate to="/client/dashboard" replace />} />
                    <Route path="/logout-callback" element={<LogoutCallbackPage />} />
                    <Route path="/client/dashboard" element={
                        <ProtectedRoute><ClientDashboardPage /></ProtectedRoute>
                    } />
                    <Route path="/client/tickets" element={
                        <ProtectedRoute><ClientTicketsPage /></ProtectedRoute>
                    } />
                    <Route path="/client/tickets/:id" element={
                        <ProtectedRoute><ClientTicketDetailPage /></ProtectedRoute>
                    } />
                    <Route path="/client/analytics" element={
                        <ProtectedRoute><ClientAnalyticsPage /></ProtectedRoute>
                    } />
                    <Route path="*" element={<Navigate to="/client/dashboard" replace />} />
                </Routes>
            </ClientLayout>
        );
    }

    const calculateLayoutWidth = () => {
        const sidebarWidth = sidebarCollapsed ? 64 : 280; // COLLAPSED_WIDTH : DRAWER_WIDTH
        return `calc(100vw - ${sidebarWidth}px)`;
    };

    // If authenticated, show the full app
    return (
        <Box className="app-container"  sx={{ display: 'flex' }}>
        <CollapsibleSidebar
        isAdmin={isAdmin} 
        onToggle={handleSidebarToggle} 
      />
             <Layout  sidebarCollapsed={sidebarCollapsed} 
                isAdmin={isAdmin}
                width={calculateLayoutWidth()} >
        <Routes>
            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="/logout-callback" element={<LogoutCallbackPage />} />

            <Route path="/" element={
                <ProtectedRoute>
                    <OverviewPage/>
                </ProtectedRoute>
            } />

            <Route path="/clients/*" element={
                <ProtectedRoute>
                  
                        <ClientsPage />
                 
                </ProtectedRoute>
            } />
            <Route path="/user" element={
                <ProtectedRoute>
                   
                        <UserProfile />
                    
                </ProtectedRoute>
            } />
             <Route path="/alerts" element={
                <ProtectedRoute>
                   
                        <AlertsPage />
                   
                </ProtectedRoute>
            } />
            <Route path="/tickets" element={
                <ProtectedRoute>

                    <TicketsPage />

                </ProtectedRoute>
            } />
            <Route path="/correlations" element={
                <ProtectedRoute>
                    <CorrelationsPage />
                </ProtectedRoute>
            } />
            <Route path="/iocs" element={
                <ProtectedRoute>
                    <IOCsPage />
                </ProtectedRoute>
            } />
            <Route path="/playbooks" element={
                <ProtectedRoute>
                    <PlaybooksPage />
                </ProtectedRoute>
            } />
            <Route path="/playbooks/sessions/:id" element={
                <ProtectedRoute>
                    <SessionDetailPage />
                </ProtectedRoute>
            } />
            <Route path="/playbooks/templates" element={
                <ProtectedRoute>
                    <PlaybookTemplatesPage />
                </ProtectedRoute>
            } />
            <Route path="/playbooks/templates/new" element={
                <ProtectedRoute>
                    <PlaybookTemplateEditorPage />
                </ProtectedRoute>
            } />
            <Route path="/playbooks/templates/:id/edit" element={
                <ProtectedRoute>
                    <PlaybookTemplateEditorPage />
                </ProtectedRoute>
            } />
            <Route path="/playbooks/review-queue" element={
                <ProtectedRoute>
                    <ReviewQueuePage />
                </ProtectedRoute>
            } />
            <Route path="/playbooks/ai-gym" element={
                <ProtectedRoute>
                    <AIGymPage />
                </ProtectedRoute>
            } />
            <Route path="/ai-performance" element={
                <ProtectedRoute>
                    <AIPerformancePage />
                </ProtectedRoute>
            } />
            <Route path="/kitchen-sink" element={
                <ProtectedRoute>
                    <KitchenSinkPage />
                </ProtectedRoute>
            } />
            <Route path="/demo" element={
                <ProtectedRoute>
                    <OverviewPageDemo />
                </ProtectedRoute>
            } />

            {/* Catch-all route for 404 Not Found */}

            <Route path="/admin" element={
                <ProtectedRoute>
                    <AdminPage />
                </ProtectedRoute>
            } />

            <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Layout>
        </Box>
    );
};

export default App;